import { isStripeCheckout } from '../../../lib/checkout-mode.js';
import { useState } from 'react';
import { LayoutGrid, ShoppingCart, Menu, Phone, Search, Bot, LayoutDashboard } from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { useTenant } from '../../../context/TenantContext.jsx';
import { showInternalTools } from '../../../lib/internal-tools.js';
import { resolveMenuRoots } from '../../../lib/db-category-tree.js';
import { TenantLogo } from '../../../components/tenant/TenantLogo.jsx';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../../components/ui/Sheet.jsx';
import { useAslCategoryTree } from '../../../lib/use-asl-category-tree.js';
import { isPlainLeftClick } from '../../../lib/click.js';
import { MobileAslNode } from './MobileAslNode.jsx';
import { buildRouteUrl } from '../../../lib/router.js';
import { isAslCategoryActive } from '../../../lib/asl-tree.js';
import { cn } from '../../../lib/utils.js';

export function SlimHeader({
  tenant,
  cartCount = 0,
  onOpenCart,
  onNavigate,
  onSearch,
  currentPath = '/',
  onOpenCopilot,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  // Demo-Tenants: interne Einstiege (Hub, ALWIS) ausblenden (qa-only F-01)
  let ctxTenant = null;
  try {
    const ctx = useTenant();
    ctxTenant = ctx?.tenant || null;
  } catch {
    // außerhalb eines TenantProvider (Tests, isoliertes Rendering)
  }
  const showInternal = showInternalTools((tenant || ctxTenant)?.settings);
  const { tree: aslCategoryTree = [], categories: dbCategories = [], status: aslCategoryTreeStatus } = useAslCategoryTree(tenant?.id ?? ctxTenant?.id);
  const aslMenuRoots = resolveMenuRoots({ aslTree: aslCategoryTree, aslStatus: aslCategoryTreeStatus, dbCategories });

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (onSearch) {
      onSearch(query);
    }
    if (onNavigate && query) {
      onNavigate(`/catalog?search=${encodeURIComponent(query)}`);
    }
  };

  const handleNav = (path) => {
    if (onNavigate) {
      onNavigate(path);
    }
  };

  const categoryUrl = (category) => category.url || buildRouteUrl({ name: 'catalog', aslCategory: category.key });
  const handleAslCategoryClick = (event, category) => {
    if (!onNavigate) return;
    // Strg/Cmd/Umschalt/Alt, Mittelklick: Browser öffnet neuen Tab bzw. Fenster, die App navigiert nicht.
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    handleNav(categoryUrl(category));
  };
  const displayCartCount = cartCount > 99 ? '99+' : String(cartCount);

  return (
    <header
      data-header-variant="slim"
      className="sticky top-0 z-40 h-14 w-full border-b border-[var(--border,var(--color-border))] bg-white/95 backdrop-blur-md transition-colors"
    >
      <div className="mx-auto flex h-full w-full max-w-[1440px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        
        {/* Brand / Logo */}
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <button
            onClick={() => handleNav('/')}
            className="flex min-w-0 items-center overflow-hidden text-left transition-opacity hover:opacity-90 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] rounded"
            aria-label="Startseite aufrufen"
          >
            <TenantLogo tenant={tenant} showSlogan={false} className="h-7 w-auto" />
          </button>
        </div>

        {/* Compact Desktop Navigation */}
        <nav aria-label="Kompakt-Navigation" className="hidden items-center gap-1 xl:flex">
          <Button
            variant={currentPath === '/catalog' || currentPath.startsWith('/catalog') ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => handleNav('/catalog')}
            className="h-8 px-2.5 text-xs font-semibold gap-1.5"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            <span>Katalog</span>
          </Button>

          {aslMenuRoots.slice(0, 4).map((cat) => {
            const isCatActive = isAslCategoryActive(currentPath, cat.key, aslMenuRoots);
            return (
              <Button
                key={cat.key}
                data-asl-category-key={cat.key}
                data-asl-level={cat.level}
                variant={isCatActive ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handleNav(categoryUrl(cat))}
                className="h-8 max-w-32 truncate px-2.5 text-xs font-medium"
              >
                {cat.name}
              </Button>
            );
          })}
        </nav>

        {/* Inline Search Bar */}
        <form onSubmit={handleSearchSubmit} className="hidden md:flex min-w-0 flex-1 max-w-sm items-center relative">
          <Search className="h-3.5 w-3.5 absolute left-3 text-muted-foreground pointer-events-none" />
          <input
            type="search"
            placeholder="Direktsuche nach SKU oder Begriff..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 w-full rounded-full border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] pl-8 pr-12 text-xs text-[var(--foreground)] placeholder:text-muted-foreground focus:bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:border-[var(--primary)] transition-all"
          />
          <span className="absolute right-2.5 top-1.5 rounded border border-[var(--border)] bg-white px-1.5 py-0.5 text-xs font-mono text-muted-foreground pointer-events-none shadow-2xs">
            Ctrl+K
          </span>
        </form>

        {/* Right Actions: Hotline, Copilot & Cart */}
        <div className="flex items-center gap-2 shrink-0">
          {tenant?.phone && (
            <a
              href={`tel:${tenant.phone.replace(/[^0-9+]/g, '')}`}
              className="hidden lg:flex items-center gap-1.5 rounded-full border border-[var(--border)] px-2.5 py-1 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--surface-container-low)] transition-colors"
              title="Hotline"
            >
              <Phone className="h-3 w-3 text-[var(--primary)]" />
              <span>{tenant.phone}</span>
            </a>
          )}

          {/* Betriebsleiter Hub Link (nur ASL-intern) */}
          {showInternal && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="executive-hub-header-trigger"
            onClick={() => handleNav('/hub')}
            className="h-8 gap-1.5 px-2.5 text-xs font-bold text-[var(--foreground)] hover:bg-[var(--surface-container-low)] cursor-pointer"
            aria-label="Betriebsleiter Hub öffnen"
          >
            <LayoutDashboard className="h-3.5 w-3.5 text-[var(--primary)]" />
            <span className="hidden xl:inline">Betriebsleiter Hub</span>
          </Button>
          )}

          {/* Copilot Assistant Trigger (nur ASL-intern) */}
          {showInternal && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="copilot-header-trigger"
            onClick={onOpenCopilot}
            className="h-8 gap-1.5 px-2.5 text-xs font-bold text-[var(--foreground)] hover:bg-[var(--surface-container-low)] cursor-pointer"
            aria-label="ALWIS Fachassistent öffnen"
          >
            <Bot className="h-3.5 w-3.5 text-[var(--primary)]" />
            <span className="hidden sm:inline">ALWIS Assistent</span>
          </Button>
          )}

          {/* Quick-Cart Button */}
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              if (onOpenCart) onOpenCart();
              else handleNav('/cart');
            }}
            className="relative h-8 gap-1.5 px-3 text-xs font-bold shadow-xs cursor-pointer"
            aria-label={`Warenkorb öffnen mit ${cartCount} Artikeln`}
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{isStripeCheckout(tenant || ctxTenant) ? 'Warenkorb' : 'Anfragekorb'}</span>
            <span
              data-testid="cart-count-badge"
              className={cn(
                'flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-bold leading-none transition-colors',
                cartCount > 0
                  ? 'bg-white text-[var(--primary,#e3000b)] font-extrabold shadow-2xs'
                  : 'bg-white/30 text-white'
              )}
            >
              {displayCartCount}
            </span>
          </Button>

          {/* Mobile Drawer Trigger */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px] lg:hidden" aria-label="Menü öffnen">
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[340px] overflow-y-auto">
              <SheetHeader className="mb-4">
                <SheetTitle className="text-left font-display">
                  <TenantLogo tenant={tenant} showSlogan={false} />
                </SheetTitle>
              </SheetHeader>

              <div className="space-y-4">
                <form onSubmit={handleSearchSubmit} className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
                  <input
                    type="search"
                    placeholder="Artikel suchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9 w-full rounded-[var(--radius)] border border-[var(--border)] pl-9 pr-3 text-xs"
                  />
                </form>

                <div className="space-y-1">
                  <SheetClose asChild>
                    <button
                      onClick={() => handleNav('/catalog')}
                      className="flex w-full items-center gap-2.5 rounded-[var(--radius)] px-3 py-2 text-left text-xs font-bold hover:bg-[var(--surface-container-low)] cursor-pointer"
                    >
                      <LayoutGrid className="h-4 w-4 text-[var(--primary)]" />
                      Gesamter Fachkatalog
                    </button>
                  </SheetClose>

                  {showInternal && (
                  <SheetClose asChild>
                    <button
                      onClick={() => handleNav('/hub')}
                      data-testid="executive-hub-mobile-trigger"
                      className="flex w-full items-center gap-2.5 rounded-[var(--radius)] px-3 py-2 text-left text-xs font-bold text-[var(--primary)] hover:bg-[var(--surface-container-low)] cursor-pointer"
                    >
                      <LayoutDashboard className="h-4 w-4 text-[var(--primary)]" />
                      Betriebsleiter Hub
                    </button>
                  </SheetClose>
                  )}

                  <div className="my-2 border-t border-[var(--border)]" />

                  {aslMenuRoots.map((category) => (
                    <MobileAslNode
                      key={category.key}
                      category={category}
                      categoryUrl={categoryUrl}
                      onCategoryClick={handleAslCategoryClick}
                      wrapLink={(anchor) => <SheetClose asChild>{anchor}</SheetClose>}
                    />
                  ))}
                </div>

                {tenant?.phone && (
                  <div className="border-t border-[var(--border)] pt-4">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Fachberater-Hotline</p>
                    <a
                      href={`tel:${tenant.phone.replace(/[^0-9+]/g, '')}`}
                      className="flex items-center gap-2 text-xs font-bold text-[var(--primary)]"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {tenant.phone}
                    </a>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

        </div>
      </div>
    </header>
  );
}
